<!--
  Erweiterte Story-Karte, NUR für die Vollbild-Übersicht (nicht die horizontal scrollende
  Reihe - dort bleibt die kompakte BetmanStoryCard.vue). Nutzt den größeren Platz im Grid für
  mehr Kontext, z.B. Erstelldatum + Teilen-Button zur statischen Betman-Seite.

  GEÄNDERT: Root-Element ist jetzt ein <div> statt <button> - ein Teilen-Button MUSS als
  eigenständiges <button>-Element rein (Klick soll NICHT auch "open" auslösen), und
  <button> innerhalb <button> ist ungültiges HTML. role="button"+tabindex+keydown übernehmen
  die Tastatur-Zugänglichkeit, die das <button>-Element vorher automatisch mitgebracht hat.

  WICHTIG: KEIN <style scoped> - gleicher Grund wie bei BetmanStoryCard.vue: scoped-Styles
  landen bei defineCustomElement NICHT im Shadow DOM. Styles liegen in style-betman-uebersicht.css.
-->
<template>
  <div
    class="story-card-erw"
    role="button"
    tabindex="0"
    @click="$emit('open')"
    @keydown.enter="$emit('open')"
    @keydown.space.prevent="$emit('open')"
  >
    <img v-if="imageUrl" :src="imageUrl" class="story-card-erw__bild" alt="" loading="lazy" decoding="async" />
    <div class="story-card-erw__inhalt">
      <div class="story-card-erw__kopf">
        <div class="story-card-erw__kopf-sub">
          <div class="story-card-erw__kopf-links">
            <span v-if="isLive" class="story-card__live-dot" aria-label="Live"></span>
            <img v-if="flaggeBild" :src="flaggeBild" :class="['story-card__flagge', `story-card__flagge--${flaggeGroesse}`]" alt="" />
            <span v-if="liga" class="story-card-erw__liga">{{ liga }}</span>
          </div>
          <div class="story-card-erw__kopf-rechts">
            <div class="story-card-erw__kopf-rechts-1">
              <span v-if="phaseLabel" :class="['story-card__phase', `story-card__phase--${phase}`]">{{ phaseLabel }}</span>
              <span v-if="isNew" class="story-card__neu">N</span>
            </div>
            <div class="story-card-erw__kopf-rechts-2">
               <span v-if="infoText" class="story-card-erw__info">{{ infoText }}</span>
            </div>
          </div>
        </div>
      </div>
      <span class="story-card-erw__label">{{ matchLabel }}</span>
      <div class="story-card-erw__fusszeile">
        <span v-if="erstelltAmFormatiert" class="story-card-erw__datum">Erstellt: {{ erstelltAmFormatiert }}</span>
        <button
          v-if="seitenUrl"
          type="button"
          class="story-card-erw__teilen-btn"
          aria-label="Teilen"
          @click.stop="teilen"
        >↗</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  matchLabel: { type: String, required: true },
  isNew: { type: Boolean, default: false },
  // Roher Phase-Schlüssel (z.B. 'preview'/'report'/'story'/'rumor') - für den CSS-Modifier,
  // robuster als aus dem übersetzten phaseLabel-Text abzuleiten.
  phase: { type: String, default: 'preview' },
  phaseLabel: { type: String, default: '' },
  infoText: { type: String, default: '' },
  isLive: { type: Boolean, default: false },
  flaggeBild: { type: String, default: null },
  flaggeGroesse: { type: String, default: 'standard' },
  // Roher Zeitstempel (ISO-String o.ä.) - wird hier selbst formatiert, analog zu
  // erstelltAmFormatiert in BetmanStoryBody.vue.
  createdAt: { type: String, default: null },
  // Thumbnail oben - Seitenverhältnis der echten Bilder ist ca. 600x312 (breit, nicht
  // quadratisch), deshalb schmaler Streifen statt Avatar-Quadrat (siehe CSS: aspect-ratio).
  imageUrl: { type: String, default: null },
  // Link zur statischen Betman-Seite (kommt aus imageBetman.url) - Button nur sichtbar,
  // wenn tatsächlich ein Link vorhanden ist.
  seitenUrl: { type: String, default: null },
  // Liga-Name (z.B. "1. Bundesliga") - kommt vom Server direkt mit der Story mit.
  liga: { type: String, default: null }
})

defineEmits(['open'])

const erstelltAmFormatiert = computed(() => {
  if (!props.createdAt) return ''
  // GEÄNDERT: Server liefert createdAt OHNE Zeitzonen-Kennzeichnung (z.B. "2026-09-06
  // 00:33:49"), meint damit aber UTC - ohne "Z"/Offset interpretieren Browser sowas
  // uneinheitlich (teils fälschlich als bereits lokale Zeit). Hier explizit als UTC parsen
  // (ISO-Format mit "T" + "Z" erzwingen), danach rechnet new Date() korrekt in die tatsächliche
  // lokale Zeitzone (z.B. CET/CEST) um.
  const isoUtc = props.createdAt.replace(' ', 'T') + (props.createdAt.endsWith('Z') ? '' : 'Z')
  const datum = new Date(isoUtc)
  if (Number.isNaN(datum.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(datum.getDate())}.${pad(datum.getMonth() + 1)}.${datum.getFullYear()} ${pad(datum.getHours())}:${pad(datum.getMinutes())}`
})

// Web-Share-API bevorzugt (nativer Teilen-Dialog auf Mobile) - Zwischenablage als Fallback für
// Desktop-Browser ohne Unterstützung. @click.stop im Template verhindert, dass der Klick
// zusätzlich "open" auf der Karte auslöst.
async function teilen() {
  if (!props.seitenUrl) return
  if (navigator.share) {
    try {
      await navigator.share({ url: props.seitenUrl, title: props.matchLabel })
    } catch {
      // Nutzer hat den Teilen-Dialog abgebrochen - kein Fehler, einfach nichts tun
    }
  } else if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(props.seitenUrl)
    } catch (e) {
      console.warn('Teilen fehlgeschlagen:', e)
    }
  }
}
</script>